import React from "react";
import { useNavigate } from "react-router-dom";
import { useContent, useProgress } from "@/lib/queries";
import { Card, Overline } from "@/components/kit";
import { Subs } from "@/components/Subs";
import { Check, Lock } from "lucide-react";

export default function Journey() {
  const navigate = useNavigate();
  const { data: content } = useContent();
  const { data: progress } = useProgress();
  if (!content || !progress) return <div className="text-inkfaint text-sm animate-pulse">Loading…</div>;

  const byId = Object.fromEntries(progress.months.map((m) => [m.id, m]));
  const me11done = byId[11]?.completed;

  return (
    <div className="em-fade-up" data-testid="journey-page">
      <Overline>Your Emergence</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2 mb-8">The Journey</h1>

      <div className="relative pl-8">
        <div className="absolute left-[11px] top-2 bottom-2 w-px bg-line" />
        <div className="space-y-3">
          {content.states.map((s) => {
            const m = byId[s.id] || {};
            const here = progress.current_month === s.id;
            const subtitle = s.id === 0
              ? "Record your starting position — the person who is here now."
              : s.enquiry;
            return (
              <button key={s.id} onClick={() => navigate(`/month/${s.id}`)} data-testid={`journey-node-${s.id}`}
                className="relative w-full text-left group">
                <span className={`absolute -left-[29px] top-4 w-6 h-6 rounded-full border flex items-center justify-center ${
                  m.completed ? "bg-gold border-gold" : here ? "border-gold bg-surface" : "border-linestrong bg-surface"
                }`}>
                  {m.completed && <Check className="w-3.5 h-3.5 text-surface" />}
                  {!m.completed && here && <span className="w-2 h-2 rounded-full bg-gold" />}
                </span>
                <div className="bg-panel border border-line rounded-2xl p-4 sm:p-5 group-hover:border-goldmuted/40 transition-colors">
                  <div className="flex items-center gap-2">
                    <span className="font-serif text-2xl text-ink"><Subs>{s.me}</Subs></span>
                    <span className="text-inkfaint">·</span>
                    <span className="text-ink">{s.theme}</span>
                    {here && <span className="text-gold text-xs ml-1">· You are here</span>}
                    {!m.completed && m.started && <span className="text-eminfo text-xs ml-1">In progress</span>}
                  </div>
                  <p className="text-inkfaint text-sm mt-1 line-clamp-2">{subtitle}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <button onClick={() => me11done && navigate("/shift")} data-testid="thennow-card"
        className={`mt-8 w-full text-left ${me11done ? "cursor-pointer" : "cursor-default"}`}>
        <Card className={me11done ? "border-goldmuted/40" : "opacity-70"}>
          <div className="flex items-start gap-4">
            {!me11done && <Lock className="w-5 h-5 text-faint mt-0.5" />}
            <div>
              <Overline>The Return</Overline>
              <div className="font-serif text-2xl text-ink mt-1"><Subs>Then & Now: ME₀ beside ME₁₁</Subs></div>
              <p className="text-inkfaint text-sm mt-1">
                {me11done ? "See everything that changed across the year." : "Unlocks when you complete ME₁₁: Emergence."}
              </p>
            </div>
          </div>
        </Card>
      </button>
    </div>
  );
}
